import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  countMissingRequiredFieldsInSection,
  groupStructuredConsultationFields,
  StructuredConsultationField,
} from '../consultationUtils';
import { ConsultationFieldInput } from './ConsultationFieldInput';
import shellStyles from './ConsultationRecordShell.module.css';
import styles from './ConsultationStructuredFieldsPanel.module.css';

const MOBILE_BREAKPOINT = '(max-width: 900px)';

const readStoredSectionKey = (storageKey?: string): string | null => {
  if (!storageKey || typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage.getItem(storageKey);
  } catch {
    return null;
  }
};

const writeStoredSectionKey = (storageKey: string | undefined, sectionKey: string) => {
  if (!storageKey || typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(storageKey, sectionKey);
  } catch {
    // Ignore storage failures.
  }
};

const useIsMobileLayout = (): boolean => {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    if (typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia(MOBILE_BREAKPOINT);
    const handleChange = () => setIsMobile(mediaQuery.matches);
    handleChange();
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return isMobile;
};

export type ConsultationStructuredFieldsPanelProps = {
  fields: StructuredConsultationField[];
  fieldValues: Record<string, string>;
  onFieldChange: (fieldId: string, value: string) => void;
  consultationId?: string;
  disabled?: boolean;
  canRemoveConsultationOnlyFields?: boolean;
  onRemoveConsultationOnlyField?: (fieldDefinitionId: string) => void;
  controlsDisabled?: boolean;
  sessionStorageKey?: string;
  highlightedFieldIds?: ReadonlySet<string>;
  forcedActiveSectionKey?: string | null;
  renderFieldInput?: (field: StructuredConsultationField) => React.ReactNode;
};

export const ConsultationStructuredFieldsPanel: React.FC<ConsultationStructuredFieldsPanelProps> = ({
  fields,
  fieldValues,
  onFieldChange,
  consultationId,
  disabled = false,
  canRemoveConsultationOnlyFields = false,
  onRemoveConsultationOnlyField,
  controlsDisabled = false,
  sessionStorageKey,
  highlightedFieldIds,
  forcedActiveSectionKey,
  renderFieldInput,
}) => {
  const { t } = useTranslation(['consultations', 'common']);
  const isMobile = useIsMobileLayout();
  const sections = React.useMemo(() => groupStructuredConsultationFields(fields), [fields]);
  const [activeSectionKey, setActiveSectionKey] = React.useState(() => {
    return readStoredSectionKey(sessionStorageKey) ?? sections[0]?.sectionKey ?? '';
  });
  const [openAccordionKey, setOpenAccordionKey] = React.useState(() => sections[0]?.sectionKey ?? '');

  React.useEffect(() => {
    if (!sections.some((section) => section.sectionKey === activeSectionKey)) {
      const nextKey = sections[0]?.sectionKey ?? '';
      setActiveSectionKey(nextKey);
      setOpenAccordionKey(nextKey);
    }
  }, [activeSectionKey, sections]);

  React.useEffect(() => {
    if (forcedActiveSectionKey) {
      setActiveSectionKey(forcedActiveSectionKey);
      setOpenAccordionKey(forcedActiveSectionKey);
    }
  }, [forcedActiveSectionKey]);

  const handleSectionChange = React.useCallback((sectionKey: string) => {
    setActiveSectionKey(sectionKey);
    writeStoredSectionKey(sessionStorageKey, sectionKey);
    if (isMobile) {
      setOpenAccordionKey(sectionKey);
    }
  }, [isMobile, sessionStorageKey]);

  const getSectionLabel = React.useCallback((label: string) => {
    if (label === 'General') {
      return t('sectionGeneral', { defaultValue: 'General' });
    }

    return label;
  }, [t]);

  const renderFieldRow = (field: StructuredConsultationField) => {
    const isInvalid = highlightedFieldIds?.has(field.id) ?? false;

    return (
      <div
        key={field.id}
        className={`${shellStyles.fieldRow} ${isInvalid ? styles.fieldRowInvalid : ''}`}
      >
        <div className={shellStyles.fieldHeader}>
          <label className={shellStyles.fieldLabel}>
            {field.name}
            {field.assignmentRequired ? <span className={shellStyles.required}>*</span> : null}
            {field.assignmentSource === 'consultation' ? (
              <span className={shellStyles.consultationOnlyBadge}>
                {t('consultationOnlyField', { defaultValue: 'Consultation only' })}
              </span>
            ) : null}
          </label>
          {canRemoveConsultationOnlyFields && field.assignmentSource === 'consultation' && onRemoveConsultationOnlyField ? (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => void onRemoveConsultationOnlyField(field.id)}
              disabled={controlsDisabled}
            >
              {t('removeField', { defaultValue: 'Remove field' })}
            </button>
          ) : null}
        </div>
        {field.description ? <p className={shellStyles.fieldDescription}>{field.description}</p> : null}
        {renderFieldInput ? renderFieldInput(field) : (
          <ConsultationFieldInput
            field={field}
            consultationId={consultationId}
            value={fieldValues[field.id] ?? ''}
            onChange={(value) => onFieldChange(field.id, value)}
            disabled={disabled}
          />
        )}
      </div>
    );
  };

  const renderSectionMeta = (section: ReturnType<typeof groupStructuredConsultationFields>[number], hasError: boolean) => {
    const missingCount = countMissingRequiredFieldsInSection(section, fieldValues);
    if (missingCount > 0) {
      return (
        <span className={`${styles.sectionMeta} ${styles.sectionMetaError}`}>
          {t('sectionRequiredRemaining', {
            defaultValue: '{{count}} required remaining',
            count: missingCount,
          })}
        </span>
      );
    }

    return (
      <span className={styles.sectionMeta}>
        {t('sectionFieldCount', {
          defaultValue: '{{count}} fields',
          count: section.fields.length,
        })}
      </span>
    );
  };

  if (sections.length === 0) {
    return null;
  }

  const activeSection = sections.find((section) => section.sectionKey === activeSectionKey) ?? sections[0];
  const showTabs = sections.length > 1 && !isMobile;

  return (
    <div className={styles.panel}>
      {showTabs ? (
        <div className={styles.sectionNav} role="tablist" aria-label={t('structuredConsultationFields', { defaultValue: 'Structured fields' })}>
          {sections.map((section) => {
            const missingCount = countMissingRequiredFieldsInSection(section, fieldValues);
            const isActive = section.sectionKey === activeSection.sectionKey;

            return (
              <button
                key={section.sectionKey}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={`${styles.sectionTab} ${isActive ? styles.sectionTabActive : ''} ${missingCount > 0 ? styles.sectionTabError : ''}`}
                onClick={() => handleSectionChange(section.sectionKey)}
              >
                <span>{getSectionLabel(section.label)}</span>
                {renderSectionMeta(section, missingCount > 0)}
              </button>
            );
          })}
        </div>
      ) : null}

      {isMobile && sections.length > 1 ? (
        <div className={styles.accordionList}>
          {sections.map((section) => {
            const missingCount = countMissingRequiredFieldsInSection(section, fieldValues);
            const isOpen = openAccordionKey === section.sectionKey;

            return (
              <div key={section.sectionKey} className={styles.accordionItem}>
                <button
                  type="button"
                  className={`${styles.accordionTrigger} ${isOpen ? styles.accordionTriggerOpen : ''}`}
                  aria-expanded={isOpen}
                  onClick={() => setOpenAccordionKey(isOpen ? '' : section.sectionKey)}
                >
                  <span>{getSectionLabel(section.label)}</span>
                  {renderSectionMeta(section, missingCount > 0)}
                </button>
                {isOpen ? (
                  <div className={styles.accordionBody}>
                    <div className={shellStyles.structuredGrid}>
                      {section.fields.map(renderFieldRow)}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <div className={shellStyles.structuredGrid} role="tabpanel">
          {activeSection.fields.map(renderFieldRow)}
        </div>
      )}
    </div>
  );
};
